import React, { useRef, useEffect } from "react";

import { ReactComponent as AIStarsIcon } from "src/images/ai-stars.svg";
import type { AIResponse } from "../../utils/types";
import ChatMessage, { hasSocialProfileLinks } from "../ChatMessage/ChatMessage";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";

interface ChatMessageListProps {
  messages: AIResponse[];
  isLoading: boolean;
  isArchived?: boolean;
  onQuickPrompt?: () => void;
  onHelpCenterLinkClick?: (url: string) => void;
}

export default function ChatMessageList({
  messages,
  isLoading,
  isArchived = false,
  onQuickPrompt,
  onHelpCenterLinkClick,
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const lastMessage = messages[messages.length - 1];
  const showTypingIndicator =
    isLoading ||
    (lastMessage?.role === "assistant" &&
      !!lastMessage.isStreaming &&
      !lastMessage.content);

  const visibleMessages = messages.filter(
    (message) => !!message.content || !!message.thinking,
  );

  return (
    <div className="ai-chat__messages">
      <div className="ai-chat__greeting">
        <AIStarsIcon />
        <div className="ai-chat__greeting-title">
          <h5 className="text-h5-semibold">
            <IDHFormattedMessage
              id="ws_ai_chat_greeting_title"
              defaultMessage="Hello there!"
            />
          </h5>
          <p className="text-h7-regular">
            <IDHFormattedMessage
              id="ws_ai_chat_greeting_subtitle"
              defaultMessage="How can I help you today?"
            />
          </p>
        </div>
      </div>
      {visibleMessages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;
        const isAssistant = message.role === "assistant";
        const showQuickPrompt =
          isLastMessage &&
          isAssistant &&
          !isLoading &&
          !message.isStreaming &&
          hasSocialProfileLinks(message.content);

        return (
          <div key={message.id}>
            <ChatMessage
              role={message.role}
              content={message.content}
              thinking={message.thinking}
              thinkingSeconds={message.thinkingSeconds}
              isStreaming={message.isStreaming}
              timestamp={message.timestamp}
              attachedFiles={message.attachedFiles}
              selectedItems={message.selectedItems}
              showQuickPrompt={showQuickPrompt}
              onQuickPrompt={onQuickPrompt}
              onHelpCenterLinkClick={onHelpCenterLinkClick}
            />
          </div>
        );
      })}
      {isArchived && (
        <div className="chat-message chat-message--assistant">
          <div className="chat-message__answer-row">
            <AIStarsIcon className="chat-message__avatar" />
            <div className="chat-message__content">
              <div className="chat-message__text">
                <IDHFormattedMessage
                  id="ws_conversation_archived_message"
                  defaultMessage="This conversation has been archived, please start a new one"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {showTypingIndicator && (
        <div className="ai-chat__typing-indicator">
          <AIStarsIcon />
          <div className="ai-chat__typing-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}
